import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Button from "../../ui/Button";
import ButtonPrev from "../../ui/ButtonPrev";
import ButtonNext from "../../ui/ButtonNext";
import { cards } from "../../utils/CardEventData";
import { getInitialCard } from "../../utils/getInitialCard";
import PopupForm from "../../ui/PopupForm";
import Carousel from "../content/Carousel";
import ShareButton from "../../ui/ShareButton";
import ShareIcon from "../../ui/svg/ShareIcon";
import SocialIcons from "./SocialIcons";

const EventItem = () => {
  const contentRef = useRef(null);
  const location = useLocation();
  const { t } = useTranslation();

  // Use the helper function to get the initial card
  const initialCard = getInitialCard();

  // Initialize state with the selected card if available
  const [selectedCard, setSelectedCard] = useState(initialCard);
  const [selectedIndex, setSelectedIndex] = useState(() => {
    if (initialCard) {
      return cards.findIndex((c) => c.id === initialCard.id);
    }
    return null;
  });
  const [visibleCards, setVisibleCards] = useState(cards.slice(0, 3));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(!!initialCard);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1280);
  const [socialOpenCards, setSocialOpenCards] = useState({});

  // Handle window resize for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1280);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update visible cards based on current index and mobile view
  useEffect(() => {
    const reversedCards = [...cards].reverse();
    setVisibleCards(
      reversedCards.slice(currentIndex, currentIndex + (isMobileView ? 1 : 3))
    );
  }, [currentIndex, isMobileView]);

  // Update meta tags if location.search changes
  useEffect(() => {
    const card = getInitialCard();
    if (card) {
      const index = cards.findIndex((c) => c.id === card.id);
      setSelectedCard(card);
      setSelectedIndex(index);
      setIsPopupOpen(true);
    }
  }, [location.search]);

  const getShareableLink = (eventId) => {
    return `https://lepenecmonitoringsolutions.eu/news-${eventId}.html`;
  };  

  const getCopyLink = (cardId) => {
    return `https://lepenecmonitoringsolutions.eu?cardId=${cardId}#news-and-events`;
  };  

  const handlePrev = () => {
    const step = isMobileView ? 1 : 3;
    const newIndex = currentIndex - step;
    const startIndex = newIndex < 0 ? cards.length - step : newIndex;
    setCurrentIndex(startIndex);
  };

  const handleNext = () => {
    const step = isMobileView ? 1 : 3;
    let newIndex = currentIndex + step;
    if (newIndex >= cards.length) {
      newIndex = 0;
    }
    setCurrentIndex(newIndex);
  };

  const openPopup = (card, index) => {
    setSelectedCard(card);
    setSelectedIndex(index);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedCard(null);
  };

  const safeStopPropagation = (e) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
  };

  const handleShare = (index) => {
    setSocialOpenCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <>
      {selectedCard && (
        <Helmet>
          <title>{`${t(selectedCard.firstTitle)} ${t(selectedCard.secoundTitle)}`}</title>
          <meta name="description" content={t(selectedCard.content)} />
          <meta
            property="og:title"
            content={`${t(selectedCard.firstTitle)} ${t(selectedCard.secoundTitle)}`}
          />
          <meta property="og:description" content={t(selectedCard.content)} />
          <meta
            property="og:image"
            content={
              selectedCard.images?.length > 0
                ? selectedCard.images[0].src
                : "https://lepenecmonitoringsolutions.eu/default-image.jpg"
            }
          />
          <meta property="og:url" content={getShareableLink(selectedCard.id)} />
          <meta property="og:type" content="article" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content={`${t(selectedCard.firstTitle)} ${t(selectedCard.secoundTitle)}`}
          />
          <meta name="twitter:description" content={t(selectedCard.content)} />
          <meta
            name="twitter:image"
            content={
              selectedCard.images?.length > 0
                ? selectedCard.images[0].src
                : "https://lepenecmonitoringsolutions.eu/default-image.jpg"
            }
          />
        </Helmet>
      )}

      <section
        id="news-and-events"
        ref={contentRef}
        className="bg-gray-100 pt-20"
      >
        <div className="text-center">
          <h2 className="text-5xl xl:text-5xl xxs:text-4xl font-bold text-green-800 py-14">
            {t("EventItem.eventTitle")}
          </h2>
        </div>

        <div
          className={`grid grid-cols-1 ${
            isMobileView
              ? "px-4"
              : "xl:grid-cols-3 gap-8 xl:gap-2 2xl:gap-8 px-24"
          } pt-8`}
        >
          {visibleCards.map((card, index) => (
            <div
              key={index}
              onClick={
                isMobileView ? () => openPopup(card, index) : undefined
              }
              className={`p-4 sm:p-8 xl:p-4 ${
                isMobileView ? "w-[75vw] mx-auto" : ""
              }`}
            >
              {isMobileView ? (
                <div className="relative bg-white rounded-sm shadow-xl overflow-hidden">
                  <div className="relative h-80 overflow-hidden">
                    <div
                      className="h-full w-full bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${card.image}), ${card.gradient}`,
                        clipPath:
                          "polygon(0 0, 100% 0, 100% 85%, 0 100%)",
                      }}
                    />
                    <h4 className="absolute bottom-2 right-4 text-white text-xl font-light uppercase text-right leading-tight">
                      <span
                        className="inline-block ml-4 xs:px-8 xs:py-3 xxs:px-2 xxs:py-1 text-base"
                        style={{ background: card.gradient }}
                      >
                        {t(card.firstTitle)}
                      </span>
                      <span
                        className="block ml-4 xs:px-8 xs:py-3 xxs:px-2 xxs:py-1 text-base"
                        style={{ background: card.gradient }}
                      >
                        {t(card.secoundTitle)}
                      </span>
                    </h4>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3 text-lg text-center">
                      {card.details.map((detail, idx) => (
                        <li key={idx} className="py-2">
                          {t(detail)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div
                    className="p-6 h-[300px] text-center pt-36"
                    style={{
                      background:
                        "linear-gradient(to right bottom, #2DB964, #14532D)",
                      clipPath:
                        "polygon(0 15%, 100% 0, 100% 100%, 0 100%)",
                    }}
                  >
                    <Button
                      className="hover:text-grey-700"
                      bgColor="bg-white"
                      textColor="text-grey-700"
                      boxShadowColor="#fff"
                      onClick={(e) => {
                        safeStopPropagation(e);
                        openPopup(card, index);
                      }}
                    >
                      {t("button.learnMore")}
                    </Button>
                    <div className="absolute bottom-2 right-2">
                      <ShareButton
                        bgColor="#fff"
                        onClick={(e) => {
                          safeStopPropagation(e);
                          handleShare(index);
                        }}
                      >
                        <ShareIcon
                          startColor="#004d00"
                          endColor="#00e600"
                        />
                      </ShareButton>
                      {socialOpenCards[index] && (
                        <div onClick={(e) => safeStopPropagation(e)}>
                          <SocialIcons
                            isOpen={true}
                            shareableLink={getShareableLink(card.id)}
                            copyLink = {getCopyLink(card.id)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <motion.div
                  className="relative bg-white rounded-sm shadow-xl overflow-hidden"
                  whileHover={{ rotateY: 180 }}
                  style={{ transformStyle: "preserve-3d" }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                >
                  <div className="relative h-80 overflow-hidden">
                    <div
                      className="h-full w-full bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${card.image}), ${card.gradient}`,
                        clipPath:
                          "polygon(0 0, 100% 0, 100% 85%, 0 100%)",
                      }}
                    />
                    <h4 className="absolute bottom-2 right-4 text-white text-xl font-light uppercase text-right leading-tight">
                      <span
                        className="inline-block ml-4 px-4 py-3"
                        style={{ background: card.gradient }}
                      >
                        {t(card.firstTitle)}
                      </span>
                      <span
                        className="block ml-4 px-4 py-3"
                        style={{ background: card.gradient }}
                      >
                        {t(card.secoundTitle)}
                      </span>
                    </h4>
                  </div>
                  <div
                    className="absolute top-0 left-0 w-full h-full opacity-0 hover:opacity-100 transition-opacity duration-300 flex justify-center items-center"
                    style={{
                      background:
                        "linear-gradient(to right bottom, #2DB964, #14532D)",
                      transform: "rotateY(180deg)",
                    }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Button
                        className="hover:text-grey-700"
                        bgColor="bg-white"
                        textColor="text-grey-700"
                        boxShadowColor="#fff"
                        onClick={(e) => {
                          safeStopPropagation(e);
                          openPopup(card, index);
                        }}
                      >
                        {t("button.learnMore")}
                      </Button>
                      <div className="absolute bottom-4 right-4">
                        <ShareButton
                          bgColor="#fff"
                          onClick={(e) => {
                            safeStopPropagation(e);
                            handleShare(index);
                          }}
                        >
                          <ShareIcon
                            startColor="#004d00"
                            endColor="#00e600"
                          />
                        </ShareButton>
                        {socialOpenCards[index] && (
                          <div onClick={(e) => safeStopPropagation(e)}>
                            <SocialIcons
                              isOpen={true}
                              shareableLink={getShareableLink(card.id)}
                              copyLink = {getCopyLink(card.id)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3 text-lg text-justify xl:h-[580px] 2xl:h-[calc(590px-10vw)]">
                      {card.details.map((detail, idx) => (
                        <li key={idx} className="py-2">
                          {t(detail)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-center items-center gap-2 pt-10">
          <div className="flex items-center justify-center space-x-28 -ml-14">
            <ButtonPrev onClick={handlePrev} />
            <ButtonNext onClick={handleNext} />
          </div>
        </div>

        {isPopupOpen && selectedCard && (
          <PopupForm isOpen={isPopupOpen} onClose={closePopup}>
            <div className="relative">
              <section className="pb-24">
                <h2 className="text-2xl text-center font-bold mb-4">
                  {t(selectedCard.firstTitle)} {t(selectedCard.secoundTitle)}
                </h2>
                <div className="flex justify-center items-center mb-4">
                  <Carousel
                    className="w-auto h-auto px-auto max-w-full max-h-full object-contain"
                    images={selectedCard?.images || []}
                  />
                </div>
                <div className="px-5">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t(selectedCard.content),
                    }}
                  />
                </div>
              </section>
              <div className="absolute bottom-2 right-2">
                <ShareButton
                  bgColor="#14532D"
                  onClick={(e) => {
                    safeStopPropagation(e);
                    handleShare(selectedIndex);
                  }}
                >
                  <ShareIcon startColor="#faf9f7" endColor="#858585" />
                </ShareButton>
                {socialOpenCards[selectedIndex] && (
                  <div onClick={(e) => safeStopPropagation(e)}>
                    <SocialIcons
                      isOpen={true}
                      bgColor="bg-green-800"
                      iconGradientStart="#faf9f7"
                      iconGradientEnd="#858585"
                      shareableLink={getShareableLink(selectedCard.id)}
                      copyLink = {getCopyLink(selectedCard.id)}
                    />
                  </div>
                )}
              </div>
            </div>
          </PopupForm>
        )}
      </section>
    </>
  );
};

export default EventItem;